import PropTypes from "prop-types"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "react-query"
import { useMarina } from "src/contexts/MarinaContext"

import Badge from "src/components/Badge"
import { statusToken } from "src/components/Badge/status_tokens"
import BaseAutocomplete from "src/components/BaseAutocomplete"
import Button from "src/components/Button"

import { searchContactsIncludingArchived } from "src/api/Contacts"

import useDebounce from "src/hooks/use_debounce"

const ROW_TYPE_ACTIVE = "active"
const ROW_TYPE_ARCHIVED = "archived"
const ROW_TYPE_NETWORK = "network"
const ROW_TYPE_DOCKWA_USER = "dockwa_user"
const ROW_TYPE_GUEST = "guest"
const ROW_TYPE_CREATE_NEW = "create_new"

const ContactAutocomplete = ({
  value,
  onChange,
  id,
  onCreateNew,
  onArchivedSelect,
  displayName,
  enableImport = false,
  disabled = false,
  guestRow,
  extraFields = [],
}) => {
  const { marinaSlug } = useMarina()
  const [searchQuery, setSearchQuery] = useState("")
  const [debouncedSetSearchQuery] = useDebounce(setSearchQuery)
  const [inputValue, setInputValue] = useState("")
  const [popupOpen, setPopupOpen] = useState(undefined)
  const inputRef = useRef(null)

  const contactId = value === "" || value == null ? null : Number(value)

  const extraFieldsKey = [...extraFields].sort().join(",")

  const { data, isFetching } = useQuery(
    [
      "contactSearchWorkOrder",
      marinaSlug,
      searchQuery,
      enableImport,
      extraFieldsKey,
    ],
    () =>
      searchContactsIncludingArchived(marinaSlug, searchQuery, {
        includeNetwork: enableImport,
        extraFields,
      }),
    {
      enabled: searchQuery.trim().length >= 3,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  )

  // Clear input when form resets contact_id externally, or show the
  // externally-supplied displayName when one is available (e.g. after an
  // inline archive restore where the consumer has the contact's name).
  useEffect(() => {
    if (!contactId) {
      setInputValue("")
      setSearchQuery("")
    } else if (displayName) {
      setInputValue(displayName)
      setSearchQuery("")
    }
  }, [contactId, displayName])

  const active = useMemo(() => data?.data ?? [], [data?.data])
  const archived = useMemo(() => data?.archived ?? [], [data?.archived])
  const networkContacts = useMemo(
    () => (enableImport ? data?.networkContacts ?? [] : []),
    [data?.networkContacts, enableImport]
  )
  const dockwaUser = useMemo(
    () => (enableImport ? data?.user ?? null : null),
    [data?.user, enableImport]
  )

  // Active rows first, then archived, then network/Dockwa-user import rows.
  // Guest row is always pinned at the bottom when present so POS staff can
  // pick it before typing anything. Each row carries its own _rowType so
  // onItemClick can route correctly.
  const apiRows = useMemo(() => {
    const rows = [
      ...active.map((c) => ({ ...c, _rowType: ROW_TYPE_ACTIVE })),
      ...archived.map((c) => ({ ...c, _rowType: ROW_TYPE_ARCHIVED })),
      ...networkContacts.map((c) => ({ ...c, _rowType: ROW_TYPE_NETWORK })),
    ]
    if (dockwaUser) rows.push({ ...dockwaUser, _rowType: ROW_TYPE_DOCKWA_USER })
    return rows
  }, [active, archived, networkContacts, dockwaUser])

  // "No results" must be derived from the actual API rows, not the
  // padded list — otherwise the synthetic Guest row in POS always
  // fills the list and the "Create a new customer" affordance never
  // appears.
  const showNoResults =
    searchQuery.trim().length >= 3 && !isFetching && apiRows.length === 0

  const taggedItems = useMemo(() => {
    if (!guestRow) return apiRows
    // The synthetic Guest pin (POS-only) prevents Autocomplete.Empty from
    // ever rendering — pad with a create-new row on zero-match so the
    // affordance is still reachable. Other surfaces keep the standard
    // noResultsContent path below.
    const rows = [
      ...apiRows,
      {
        id: "contact-autocomplete-guest",
        name: guestRow.label,
        _rowType: ROW_TYPE_GUEST,
      },
    ]
    if (showNoResults && onCreateNew) {
      rows.push({
        id: "contact-autocomplete-create-new",
        name: "Create a new customer",
        _rowType: ROW_TYPE_CREATE_NEW,
      })
    }
    return rows
  }, [apiRows, guestRow, showNoResults, onCreateNew])

  const handleValueChange = (nextValue) => {
    setInputValue(nextValue)
    debouncedSetSearchQuery(nextValue)

    // If user clears the input, clear the selection
    if (nextValue === "" && contactId) {
      onChange("")
    }
  }

  const handleItemClick = (taggedContact) => {
    const { _rowType, ...contact } = taggedContact
    if (_rowType === ROW_TYPE_ARCHIVED) {
      onArchivedSelect?.(contact)
      return
    }
    if (_rowType === ROW_TYPE_NETWORK) {
      const path = data?.brewerImportPath
      if (path)
        window.open(`${path}/${contact.encodedId}`, "_blank", "noreferrer")
      return
    }
    if (_rowType === ROW_TYPE_DOCKWA_USER) {
      const path = data?.userImportPath
      if (path) window.open(`${path}/${contact.id}`, "_blank", "noreferrer")
      return
    }
    if (_rowType === ROW_TYPE_GUEST) {
      guestRow.onSelect()
      setInputValue("")
      setSearchQuery("")
      return
    }
    if (_rowType === ROW_TYPE_CREATE_NEW) {
      handleCreateNew()
      return
    }
    setInputValue(contact.name)
    setSearchQuery("")
    onChange(contact.id ?? "", contact)
  }

  const handleCreateNew = () => {
    setPopupOpen(false)
    setInputValue("")
    setSearchQuery("")
    inputRef.current?.blur()
    // Reset controlled open to uncontrolled after the popup closes
    setTimeout(() => setPopupOpen(undefined), 0)
    onCreateNew()
  }

  // When the synthetic Guest row is present (POS) we always want it visible
  // regardless of input text; the server already filtered the other rows
  // by query, so let every row through. Other surfaces keep Base UI's
  // default client-side match.
  const autocompleteFilter = guestRow ? () => true : null

  const noResultsContent = showNoResults ? (
    <div className="text-sm text-gray-500">
      No customers found.{" "}
      {onCreateNew ? (
        <Button variant="link" onClick={handleCreateNew}>
          Create a new customer →
        </Button>
      ) : (
        <a
          href={`/manage/${marinaSlug}/contacts/new`}
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 hover:underline"
        >
          Create a new customer →
        </a>
      )}
    </div>
  ) : null

  return (
    <BaseAutocomplete
      id={id}
      value={inputValue}
      onValueChange={handleValueChange}
      items={taggedItems}
      onItemClick={handleItemClick}
      itemToStringValue={(contact) => contact?.name ?? ""}
      isLoading={isFetching}
      placeholder="Search by name, email, or phone..."
      disabled={disabled}
      inputRef={inputRef}
      filter={autocompleteFilter}
      open={popupOpen}
      onOpenChange={(isOpen) => {
        if (popupOpen !== undefined) setPopupOpen(isOpen)
      }}
      renderOption={({ option }) => {
        const isArchivedRow = option._rowType === ROW_TYPE_ARCHIVED
        const isNetworkRow = option._rowType === ROW_TYPE_NETWORK
        const isDockwaUserRow = option._rowType === ROW_TYPE_DOCKWA_USER
        const isGuestRow = option._rowType === ROW_TYPE_GUEST
        const isCreateNewRow = option._rowType === ROW_TYPE_CREATE_NEW
        if (isGuestRow) {
          return (
            <div className="flex w-full items-center py-0.5">
              <i className="fa-solid fa-user mr-2 text-gray-500" />
              <span className="font-medium">{option.name}</span>
            </div>
          )
        }
        if (isCreateNewRow) {
          return (
            <div className="flex w-full items-center py-0.5 text-blue-600">
              <i className="fa-solid fa-plus mr-2" />
              <span className="font-medium">{option.name} →</span>
            </div>
          )
        }
        return (
          <div
            className={`flex w-full flex-col py-0.5 ${
              isArchivedRow ? "text-gray-500" : ""
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{option.name}</span>
              {isArchivedRow && (
                <Badge
                  color={statusToken("Archived")}
                  text="Archived"
                  variant="small"
                />
              )}
              {isNetworkRow && (
                <Badge
                  color="info"
                  text="Import from Safe Harbor"
                  variant="small"
                />
              )}
              {isDockwaUserRow && (
                <Badge color="info" text="Import from Dockwa" variant="small" />
              )}
            </div>
            <span className="text-xs">
              {[option.email, option.displayPhone].filter(Boolean).join(" · ")}
            </span>
          </div>
        )
      }}
      noResultsContent={noResultsContent}
    />
  )
}

ContactAutocomplete.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  id: PropTypes.string,
  onCreateNew: PropTypes.func,
  onArchivedSelect: PropTypes.func,
  displayName: PropTypes.string,
  enableImport: PropTypes.bool,
  disabled: PropTypes.bool,
  guestRow: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onSelect: PropTypes.func.isRequired,
  }),
  extraFields: PropTypes.arrayOf(PropTypes.string),
}

export default ContactAutocomplete
